/* La Sura - helpers for the builder's Step by step layout: which blocks make
   which step, the fields in a step, the total in the Back and Continue bar,
   and the analytics events. Split from lasura-cake-steps.js to keep each
   script under Theme Check's 10 KB limit. Load this first. */
(() => {
  /* The steps, in page order after the steps block (host):
       1. every block up to and including Horizon's variant picker (size, height);
          with "One question per step" on, one step per option (Shape, Size,
          Layers), shown one at a time by data-lasura-part on the column
       2. each builder group, where groups sharing a data-step value share a step
       3. the review: every block after the builder (summary, buy buttons...)
     Blocks before the host (title, price, preview) stay in view throughout. */
  const collect = (host, cfg) => {
    const siblings = [...host.parentElement.children];
    const after = siblings.slice(siblings.indexOf(host) + 1);
    const pickerAt = after.findIndex((el) => el.matches('variant-picker') || el.querySelector('variant-picker'));
    const builderAt = after.findIndex((el) => el.matches('lasura-cake-builder') || el.querySelector('lasura-cake-builder'));
    const steps = [];
    if (pickerAt >= 0) {
      const els = after.slice(0, pickerAt + 1);
      const picker = after[pickerAt].matches('variant-picker') ? after[pickerAt] : after[pickerAt].querySelector('variant-picker');
      const legends = [...picker.querySelectorAll('fieldset')].map((f) => f.querySelector('legend')?.childNodes[0]?.textContent.trim());
      if (cfg.split && legends.length > 1) legends.forEach((title, i) => steps.push({ title: title || cfg.sizeTitle, els, picker, part: i + 1 }));
      else steps.push({ title: cfg.sizeTitle, els });
    }
    if (builderAt >= 0) {
      const builder = after[builderAt].matches('lasura-cake-builder') ? after[builderAt] : after[builderAt].querySelector('lasura-cake-builder');
      const groups = builder.querySelector('.lasura-builder__groups');
      [...(groups ? groups.children : [])].forEach((el) => {
        if (!el.firstElementChild && !el.textContent.trim()) return;
        /* A theme editor hint belongs to the step before it, not a step of its own. */
        if (el.matches('.lasura-group__editor-hint, .lasura-extras__hint')) return steps[steps.length - 1]?.els.push(el);
        const key = el.dataset.step || el.querySelector('[data-step]')?.dataset.step || '';
        const last = steps[steps.length - 1];
        if (key && last && last.key === key) last.els.push(el);
        else steps.push({ key, title: key || el.dataset.groupLabel || el.querySelector('legend, label, .lasura-ref__legend')?.textContent.trim(), els: [el] });
      });
      /* The builder's wrapper and the divider above it show only on the builder's own steps:
         on the size step and the review they were an empty gap and a second divider. */
      steps.shell = { builder: after[builderAt], els: [...after.slice(Math.max(pickerAt + 1, 0), builderAt), after[builderAt]] };
    }
    const review = after.slice(builderAt >= 0 ? builderAt + 1 : pickerAt + 1).filter((el) => !el.matches('[data-steps-nav]'));
    if (review.length) steps.push({ title: cfg.reviewTitle, els: review, review: true });
    return steps;
  };

  /* A one-question step's fieldset, looked up each time: Horizon morphs the
     picker's markup on every change. */
  const partOf = (step) => step.part && step.picker.querySelectorAll('fieldset')[step.part - 1];

  const fields = (step) =>
    (step.part ? [partOf(step)].filter(Boolean) : step.els)
      .flatMap((el) => [...el.querySelectorAll('input, select, textarea')])
      .filter((input) => !input.disabled && input.type !== 'hidden');

  /* Options at different prices (a size, Square at +£5): the step waits for
     Continue rather than moving on at a tap, so the difference is seen (Matt,
     2026-10-10: moving on discouraged a bigger size). The prices shown are
     the variant notes' and the option cards', Included or +£ against the cheapest. */
  /* The bar's height, for the page's scroll padding on phones
     (blocks/lasura-cake-steps.liquid): measured, as it changes with the
     allergen line, so a tapped choice half under it is revealed by just
     enough. */
  document.addEventListener('lasura:steps-ready', () => {
    const nav = document.querySelector('.lasura-steps-on > .lasura-steps__nav');
    if (!nav || !('ResizeObserver' in window)) return;
    new ResizeObserver(() => document.documentElement.style.setProperty('--lasura-steps-bar', `${nav.offsetHeight}px`)).observe(nav);
  });

  const priced = (step) =>
    new Set(
      (step.part ? [partOf(step)].filter(Boolean) : step.els).flatMap((el) =>
        [...el.querySelectorAll('.lasura-variant-note__price, .lasura-opt__price')].map((price) => price.textContent.trim())
      )
    ).size > 1;

  /* Whether a step holds a field (a refused add, a choice opened from the list). */
  const holds = (step, target) => (step.part ? !!partOf(step)?.contains(target) : step.els.some((el) => el.contains(target)));

  /* Nothing required and nothing picked or typed yet: Continue reads Skip. */
  const optionalEmpty = (step) => {
    const all = fields(step);
    const required = all.some((f) => f.required);
    const filled = all.some((f) => (f.type === 'radio' || f.type === 'checkbox' ? f.checked && f.value : f.type === 'file' ? f.files?.length : f.value.trim()));
    return !step.review && !required && !filled;
  };

  /* Copies the cake summary's total into the Back and Continue bar. */
  const mirrorTotal = (column, nav) => {
    const source = column.querySelector('[data-summary-total]');
    const box = nav.querySelector('[data-steps-total]');
    if (!source || !box) return;
    const copy = () => {
      box.querySelector('[data-steps-total-value]').textContent = source.textContent;
      box.hidden = !source.textContent.trim();
    };
    new MutationObserver(copy).observe(source, { childList: true, characterData: true, subtree: true });
    copy();
  };

  const track = (name, data = {}) => {
    const entry = new URLSearchParams(location.search).get('from') || '';
    const detail = { layout: 'steps', entry, ...data };
    document.dispatchEvent(new CustomEvent(`lasura:${name}`, { detail }));
    try {
      window.Shopify?.analytics?.publish?.(name, detail);
    } catch {}
  };

  /* One heading row (Matt, 2026-09-30): the step's name large, "2 of 12"
     beside it, the whole sentence ("Step 2 of 12: Size") for screen readers.
     The step's own legend, when it only repeats the name, is hidden
     visually (it still names its choices). */
  const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
  const span = (cls, text, hide) => {
    const el = document.createElement('span');
    el.className = cls;
    el.textContent = text;
    if (hide) el.setAttribute('aria-hidden', 'true');
    return el;
  };
  const heading = (label, step, full, count) => {
    if (step.title && count) label.replaceChildren(span('visually-hidden', full), span('lasura-steps__title', step.title, true), span('lasura-steps__count', count, true));
    else label.textContent = full;
    document.querySelectorAll('.lasura-step-dup').forEach((el) => el.classList.remove('lasura-step-dup'));
    const legend = step.els.flatMap((el) => [...el.querySelectorAll('legend, [class$="__legend"]')]).find((el) => el.getClientRects().length);
    if (legend && norm(legend.textContent) === norm(step.title)) legend.classList.add('lasura-step-dup');
  };

  /* Scrolls only if the step's heading is out of view (Matt, 2026-09-27),
     and only as far as the heading. With the phone strip on, the heading
     lands just under it: the strip keeps the cake in view, so the page
     doesn't run back up to the big picture on every tap (Matt, 2026-09-28).
     A missing choice uses it too (lasura-cake-builder.js), so the page no
     longer centres the field and hides the heading (Matt, 2026-09-30). */
  const bring = (ctl) => {
    const r = ctl.ui.getBoundingClientRect(), px = (el, p) => parseFloat(getComputedStyle(el)[p]) || 0;
    const strip = ctl.column.querySelector('lasura-cake-preview[data-strip-on]') && document.querySelector('.lasura-cake-strip');
    let at = strip ? (parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0) + strip.offsetHeight + 12 : px(ctl.ui, 'scrollMarginTop');
    /* The pinned rail (Sura and the stages, lasura-cake-fs-more.js) covers the top of the step:
       the heading lands just under it (Matt, 2026-10-19, teardown BLD-5). */
    const rail = document.querySelector('.lasura-fs-rail');
    const pinned = rail && ['sticky', 'fixed'].includes(getComputedStyle(rail).position) && rail.getClientRects().length;
    if (pinned) at = Math.max(at, Math.min(rail.getBoundingClientRect().bottom, innerHeight / 2) + 12);
    if (r.top < at - 4 || r.bottom > innerHeight - px(document.documentElement, 'scrollPaddingBottom')) {
      const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (strip || pinned) scrollTo({ top: scrollY + r.top - at, behavior });
      else ctl.ui.scrollIntoView({ block: 'start', behavior });
    }
  };

  /* What the last step's button presses: an enquiry when a made-to-quote
     choice is picked, otherwise the page's own add button (the one the cake
     summary's bar presses too). */
  const addTarget = () => {
    const sum = document.querySelector('lasura-cake-summary');
    return sum?.enquiryLink?.() || sum?.realButton?.() || document.querySelector('.lasura-steps-on [data-builder-enquiry]:not([hidden]) a, .lasura-steps-on button[type="submit"][name="add"]');
  };

  window.LasuraCakeStepsParts = {
    addTarget, collect, fields, priced, holds, optionalEmpty, mirrorTotal, track, heading, bring };
})();
